import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    InputAdornment,
    Menu,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import useApiData from "../../services/useApiData.js";
import api from "../../services/axiosInterceptor.js";
import PostData from "../../services/PostData.js";
import SnackbarEx from "../Helpers/SnackbarEx.jsx";
import ConfirmDeleteDialog from "../Helpers/ConfirmDeleteDialog.jsx";
import ProductFormDialog from "./ProductFormDialog.jsx";
import EbayCampaignDialog from "./EbayCampaignDialog.jsx";
import MigrateEbayRecoveryDialog from "./MigrateEbayRecoveryDialog.jsx";
import GridFilterBar, { useGridFilter } from "../Helpers/GridFilterBar.jsx";
import GridLayoutControls from "../Helpers/GridLayoutControls.jsx";
import useGridLayout from "../Helpers/useGridLayout.js";
import { GRID_LAYOUT_KEYS } from "../Helpers/gridLayoutKeys.js";
import { PRODUCT_FILTER_FIELDS } from "../Helpers/gridFilterFields.js";
import {
    catalogDataGridSx,
    catalogDangerButtonSx,
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
    appDataGridDefaultProps,
    appDataGridSlotProps,
} from "./catalogGridStyles.js";
import GridRowContextMenu, {
    buildProductShareUrl,
    copyTextToClipboard,
    useGridRowContextMenu,
} from "../Helpers/GridRowContextMenu.jsx";

const DEFAULT_PRODUCT_SORT = [{ field: "totalQuantOnHand", sort: "desc" }];
const CLIENT_ONLY_FILTER_FIELDS = new Set([
    "statusChecks",
    "listedPlatformsText",
    "ebayNeedsMigrate",
]);

function namesOf(value) {
    if (!Array.isArray(value)) return "";
    return value
        .map((entry) => (entry && typeof entry === "object" ? entry.name || "" : ""))
        .filter(Boolean)
        .join(", ");
}

function countOf(value) {
    return Array.isArray(value) ? value.length : 0;
}

function displayNum(value) {
    return value == null || value === "" ? "" : value;
}

function moneyText(value) {
    if (value == null || value === "") return "";
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function percentText(value) {
    if (value == null || value === "") return "";
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return `${(n * 100).toFixed(1)}%`;
}

function isEbayMigrateEligible(row) {
    const sku = String(row?.internalSkuId || "").trim();
    const offerId = String(row?.ebayOfferId || "").trim();
    return Boolean(sku) && !offerId;
}

function isEbayCampaignEligible(row) {
    return Boolean(String(row?.ebayListingId || "").trim());
}

/** On eBay (listed or qty on hand) but missing Inventory offer ID — user should migrate. */
function computeEbayNeedsMigrate(platforms, offerId, availableQuantityEbay) {
    if (String(offerId || "").trim()) return false;
    const listed = Array.isArray(platforms) && platforms.includes("eBay");
    const qty = Number(availableQuantityEbay);
    const hasEbayQty = Number.isFinite(qty) && qty > 0;
    return listed || hasEbayQty;
}

function dateText(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString();
}

function createdByText(value) {
    if (!value) return "";
    if (typeof value === "string") return value;
    return value.name || value.email || "";
}




function wrapCell(value) {
    const text = value == null || value === "" ? "" : String(value);
    if (!text) {
        return (
            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                —
            </Typography>
        );
    }
    return (
        <Tooltip title={text} enterDelay={300} placement="top-start">
            <Box
                component="span"
                sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%",
                    display: "block",
                }}
            >
                {text}
            </Box>
        </Tooltip>
    );
}

function conditionChip(value) {
    const text = value == null || value === "" ? "" : String(value);
    if (!text) return wrapCell("");
    return (
        <Chip
            size="small"
            label={text}
            sx={{
                height: 24,
                fontWeight: 600,
                fontSize: 12,
                borderRadius: "8px",
                backgroundColor: "#f1f5f9",
                color: "#475569",
            }}
        />
    );
}

const LISTED_CHIP_SX = {
    Walmart: { backgroundColor: "#e8f5e9", color: "#2e7d32" },
    eBay: { backgroundColor: "#e3f2fd", color: "#1565c0" },
    Shopify: { backgroundColor: "#f3e5f5", color: "#6a1b9a" },
};

function listedPlatformChips(platforms, { needsEbayMigrate = false } = {}) {
    const list = Array.isArray(platforms) ? platforms.filter(Boolean) : [];
    if (!list.length && !needsEbayMigrate) return wrapCell("");
    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", py: 0.5 }}>
            {list.map((platform) => (
                <Chip
                    key={platform}
                    size="small"
                    label={platform}
                    sx={{
                        height: 22,
                        fontWeight: 600,
                        fontSize: 11,
                        borderRadius: "8px",
                        ...(LISTED_CHIP_SX[platform] || {
                            backgroundColor: "#f1f5f9",
                            color: "#475569",
                        }),
                    }}
                />
            ))}
            {needsEbayMigrate ? (
                <Tooltip title="Has eBay quantity or listed on eBay but missing offer ID — use Migrate to eBay">
                    <Chip
                        size="small"
                        label="Needs migrate"
                        sx={{
                            height: 22,
                            fontWeight: 700,
                            fontSize: 11,
                            borderRadius: "8px",
                            backgroundColor: "#fff7ed",
                            color: "#c2410c",
                            border: "1px solid #fdba74",
                        }}
                    />
                </Tooltip>
            ) : null}
        </Box>
    );
}

function formatQuantitySyncErrors(errors) {
    const list = Array.isArray(errors)
        ? errors.filter((e) => e && e.error !== false)
        : [];
    if (!list.length) {
        return {
            quantitySyncErrors: [],
            platformErrorPlatforms: "",
            platformErrorAt: "",
        };
    }
    const platforms = list.map((e) => e.platform).filter(Boolean);
    const latestAt = list
        .map((e) => (e.at ? new Date(e.at) : null))
        .filter((d) => d && !Number.isNaN(d.getTime()))
        .sort((a, b) => b - a)[0];
    return {
        quantitySyncErrors: list,
        platformErrorPlatforms: platforms.join(", "),
        platformErrorAt: latestAt ? dateText(latestAt) : "",
    };
}

const PLATFORM_ERROR_FILTERS = [
    { id: "all", label: "All" },
    { id: "eBay", label: "eBay" },
    { id: "Walmart", label: "Walmart" },
    { id: "Shopify", label: "Shopify" },
];

export default function ProductGrid({ platformErrorsOnly = false } = {}) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [rows, setRows] = useState([]);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const [searchInput, setSearchInput] = useState(
        () => searchParams.get("q") || ""
    );
    const [searchQ, setSearchQ] = useState(
        () => (searchParams.get("q") || "").trim()
    );
    const [errorPlatformFilter, setErrorPlatformFilter] = useState("all");
    const [rowCount, setRowCount] = useState(0);
    const [selectionModel, setSelectionModel] = useState({
        type: "include",
        ids: new Set(),
    });
    const selectedIds = useMemo(
        () => [...(selectionModel?.ids ?? [])],
        [selectionModel]
    );
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [highlightField, setHighlightField] = useState(null);
    const [remigrateAfterSaveId, setRemigrateAfterSaveId] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [migrating, setMigrating] = useState(false);
    const [campaignSubmitting, setCampaignSubmitting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
    const [campaignTargetIds, setCampaignTargetIds] = useState([]);
    const [migrateRecovery, setMigrateRecovery] = useState(null);
    const [platformMenuAnchor, setPlatformMenuAnchor] = useState(null);
    const [snack, setSnack] = useState({ open: false, color: "", message: "" });
    const {
        menu: rowMenu,
        close: closeRowMenu,
        rowContextMenuSlotProps,
    } = useGridRowContextMenu();

    useEffect(() => {
        const t = setTimeout(() => {
            const nextQ = searchInput.trim();
            setSearchQ(nextQ);
            setPaginationModel((prev) =>
                prev.page === 0 ? prev : { ...prev, page: 0 }
            );
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (nextQ) next.set("q", nextQ);
                    else next.delete("q");
                    if (next.toString() === prev.toString()) return prev;
                    return next;
                },
                { replace: true }
            );
        }, 300);
        return () => clearTimeout(t);
    }, [searchInput, setSearchParams]);

    const shareTab = searchParams.get("t") || "0";

    const { data: fieldMeta } = useApiData(
        "api/broylesllc/catalog-field-meta"
    );
    const productMeta = fieldMeta?.product || {};
    const filterFields = useMemo(() => PRODUCT_FILTER_FIELDS(productMeta), [productMeta]);
    const {
        apply: applyFilter,
        activeConditions,
        conjunction,
        barProps: filterBar,
    } = useGridFilter(filterFields);

    const layout = useGridLayout(
        platformErrorsOnly
            ? GRID_LAYOUT_KEYS.productPlatformErrors
            : GRID_LAYOUT_KEYS.products,
        {
            sortModel: DEFAULT_PRODUCT_SORT,
            columnVisibilityModel: {},
        }
    );

    const clientOnlyFilterActive = useMemo(
        () => activeConditions.some((c) => CLIENT_ONLY_FILTER_FIELDS.has(c.field)),
        [activeConditions]
    );

    const serverFilterConditions = useMemo(
        () => activeConditions.filter((c) => !CLIENT_ONLY_FILTER_FIELDS.has(c.field)),
        [activeConditions]
    );

    const filtersParam = useMemo(() => {
        if (!serverFilterConditions.length) return "";
        return JSON.stringify({
            conjunction,
            conditions: serverFilterConditions.map(({ field, operator, value }) => ({
                field,
                operator,
                value,
            })),
        });
    }, [serverFilterConditions, conjunction]);

    const sortForApi = useMemo(() => {
        const entry = layout.sortModel?.[0];
        if (entry?.field) {
            return {
                sort: entry.field,
                sortDir: entry.sort === "asc" ? "asc" : "desc",
            };
        }
        return { sort: "totalQuantOnHand", sortDir: "desc" };
    }, [layout.sortModel]);

    useEffect(() => {
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [filtersParam, conjunction, clientOnlyFilterActive, errorPlatformFilter]);

    const listUrl = useMemo(() => {
        const params = new URLSearchParams({
            page: String(clientOnlyFilterActive ? 1 : paginationModel.page + 1),
            pageSize: String(clientOnlyFilterActive ? 500 : paginationModel.pageSize),
            sort: sortForApi.sort,
            sortDir: sortForApi.sortDir,
        });
        if (searchQ) params.set("q", searchQ);
        if (filtersParam) params.set("filters", filtersParam);
        if (platformErrorsOnly) {
            params.set("hasQuantitySyncErrors", "1");
            if (
                errorPlatformFilter === "eBay" ||
                errorPlatformFilter === "Walmart" ||
                errorPlatformFilter === "Shopify"
            ) {
                params.set("quantitySyncErrorPlatform", errorPlatformFilter);
            }
        }
        return `api/broylesllc/products?${params.toString()}`;
    }, [
        paginationModel.page,
        paginationModel.pageSize,
        searchQ,
        filtersParam,
        clientOnlyFilterActive,
        sortForApi.sort,
        sortForApi.sortDir,
        platformErrorsOnly,
        errorPlatformFilter,
    ]);

    const { data, error, load, isLoading, refetch } = useApiData(listUrl);

    useEffect(() => {
        const items = data?.items || [];
        setRowCount(typeof data?.total === "number" ? data.total : 0);
        setRows(
            items.map((p) => ({
                id: p._id,
                listingField: p.listingField || "",
                itemName: p.itemName || "",
                brand: namesOf(p.brand),
                supportingPhotos: countOf(p.supportingPhotos) || "",
                azCategory: p.azCategory || "",
                ebayCategory: p.ebayCategory || "",
                size: namesOf(p.size),
                totalQuantity: displayNum(p.totalQuantity),
                innerPacks: displayNum(p.innerPacks),
                partialPackEligible: p.partialPackEligible || "",
                colorFlavor: namesOf(p.colorFlavor),
                model: namesOf(p.model),
                gender: p.gender || "",
                conditionGrade: p.conditionGrade || "",
                statusChecks: namesOf(p.statusChecks),
                positiveExamples: countOf(p.positiveExamples) || "",
                negativeExamples: countOf(p.negativeExamples) || "",
                upc: p.upc || "",
                amazonUrl: p.amazonUrl || "",
                asin: p.asin || "",
                gtin: p.gtin || "",
                walmartUrl: p.walmartUrl || "",
                wpid: p.wpid || "",
                msrpUsd: displayNum(p.msrpUsd),
                velocityAmazonSalesPerMonth: displayNum(p.velocityAmazonSalesPerMonth),
                vistaMaxPrice: displayNum(p.vistaMaxPrice),
                macMaxPrice: displayNum(p.macMaxPrice),
                bidftaMaxPrice: displayNum(p.bidftaMaxPrice),
                ffPricing: displayNum(p.ffPricing),
                fbMktPricing: displayNum(p.fbMktPricing),
                purchasePriceEbay: displayNum(p.purchasePriceEbay),
                weightLbs: displayNum(p.weightLbs),
                heightIn: displayNum(p.heightIn),
                widthIn: displayNum(p.widthIn),
                depthIn: displayNum(p.depthIn),
                costRollup1x: displayNum(p.costRollup1x),
                costRollup2x: displayNum(p.costRollup2x),
                costRollup3x: displayNum(p.costRollup3x),
                costRollup4x: displayNum(p.costRollup4x),
                packagingType: p.packagingType || "",
                estShippingCost: displayNum(p.estShippingCost),
                ebayShippingPolicy: p.ebayShippingPolicy || "",
                ebayDescriptionTemplate: p.ebayDescriptionTemplate || "",
                walmartBlocked: p.walmartBlocked || "",
                suggestedEbayPrice: displayNum(p.suggestedEbayPrice),
                suggestedWalmartPrice: displayNum(p.suggestedWalmartPrice),
                suggestedMargin: displayNum(p.suggestedMargin),
                catalogItems: countOf(p.catalogItemAirtableIds) || "",
                createdBy: createdByText(p.createdByAirtable),
                viablePackCount: displayNum(p.viablePackCount),
                vistaPackPrice: displayNum(p.vistaPackPrice),
                createdTime: dateText(p.airtableCreatedTime || p.createdAt),
                internalSkuId: p.internalSkuId || "",
                walmartSkuLink: p.walmartLinkedSku || countOf(p.walmartSkuLinkAirtableIds) || "",
                ebaySkuLink: p.ebayLinkedSku || countOf(p.ebaySkuLinkAirtableIds) || "",
                listedPlatforms: Array.isArray(p.listedPlatforms) ? p.listedPlatforms : [],
                listedPlatformsText: Array.isArray(p.listedPlatforms)
                    ? p.listedPlatforms.join(", ")
                    : "",
                ebayOfferId: p.ebayOfferId || "",
                ebayListingId: p.ebayListingId || "",
                ebayNeedsMigrate: computeEbayNeedsMigrate(
                    p.listedPlatforms,
                    p.ebayOfferId,
                    p.availableQuantityEbay
                ),
                walmartPrice: displayNum(p.walmartPrice),
                ebayPrice: displayNum(p.ebayPrice),
                shopifyPrice: displayNum(p.shopifyPrice),
                shopifyProductId: p.shopifyProductId || "",
                shopifyVariantId: p.shopifyVariantId || "",
                shopifySkuLink: p.shopifyLinkedSku || "",
                helperFlagWalmartAutomation: p.helperFlagWalmartAutomation || "",
                availableQuantityEbay: displayNum(p.availableQuantityEbay),
                availableQuantityWalmart: displayNum(p.availableQuantityWalmart),
                availableQuantityShopify: displayNum(p.availableQuantityShopify),
                descriptionTemplateValueEbayConfig: p.descriptionTemplateValueEbayConfig || "",
                ebayConditionField: p.ebayConditionField || "",
                volumePricing2x: displayNum(p.volumePricing2x),
                volumePricing3x: displayNum(p.volumePricing3x),
                volumePricing4x: displayNum(p.volumePricing4x),
                table21: countOf(p.table21AirtableIds) || "",
                ebayPublicInterfaceView: p.ebayPublicInterfaceView || "",
                ebayQuant30PlusDays: displayNum(p.ebayQuant30PlusDays),
                walmartQuant30PlusDays: displayNum(p.walmartQuant30PlusDays),
                ebayOlderListingAge: displayNum(p.ebayOlderListingAge),
                walmartOlderListingAge: displayNum(p.walmartOlderListingAge),
                totalQuantOnHand: displayNum(p.totalQuantOnHand),
                oldestPurchaseDate: p.oldestPurchaseDate
                    ? new Date(p.oldestPurchaseDate).toLocaleDateString()
                    : "",
                expirationDateRollup: p.expirationDateRollup
                    ? new Date(p.expirationDateRollup).toLocaleDateString()
                    : "",
                inspectionWizardBuiltSku: p.inspectionWizardBuiltSku ? "Yes" : "",
                ...formatQuantitySyncErrors(p.quantitySyncErrors),
            }))
        );
    }, [data]);

    const filteredRows = useMemo(() => {
        if (!clientOnlyFilterActive) return rows;
        return applyFilter(rows);
    }, [rows, applyFilter, clientOnlyFilterActive]);

    const gridSlotProps = useMemo(
        () => rowContextMenuSlotProps(filteredRows, appDataGridSlotProps),
        [rowContextMenuSlotProps, filteredRows]
    );

    const baseColumns = useMemo(() => {
        const text = (field, headerName, width = 150) => ({
            field,
            headerName,
            width,
            minWidth: 90,
            renderCell: (params) => wrapCell(params.value),
        });
        const money = (field, headerName, width = 130) => ({
            field,
            headerName,
            width,
            minWidth: 100,
            align: "right",
            headerAlign: "right",
            renderCell: (params) => wrapCell(moneyText(params.value)),
        });
        const number = (field, headerName, width = 110) => ({
            field,
            headerName,
            width,
            minWidth: 80,
            type: "number",
            align: "right",
            headerAlign: "right",
        });
        const percent = (field, headerName) => ({
            field,
            headerName,
            width: 140,
            minWidth: 100,
            align: "right",
            headerAlign: "right",
            renderCell: (params) => wrapCell(percentText(params.value)),
        });
        const platformErrorColumns = [
            {
                field: "platformErrorPlatforms",
                headerName: "Platform",
                width: 140,
                minWidth: 110,
                renderCell: (params) =>
                    listedPlatformChips(
                        String(params.value || "")
                            .split(",")
                            .map((s) => s.trim())
                            .filter(Boolean)
                    ),
            },
            text("platformErrorAt", "Error At", 170),
        ];

        return [
            ...(platformErrorsOnly ? platformErrorColumns : []),
            { ...text("listingField", "Listing Field", 280), flex: 1.2, minWidth: 200 },
            text("itemName", "Item Name", 220),
            text("brand", "Brand", 140),
            number("supportingPhotos", "Supporting Photos", 150),
            text("azCategory", "AZ Category", 160),
            text("ebayCategory", "eBay Category", 170),
            text("size", "Size", 120),
            number("totalQuantity", "Total Quantity", 120),
            number("innerPacks", "Inner Packs", 110),
            text("partialPackEligible", "Partial Pack Eligible", 160),
            text("colorFlavor", "Color / Flavor", 160),
            text("model", "Model", 140),
            text("gender", "Gender", 110),
            {
                field: "conditionGrade",
                headerName: "Condition Grade",
                width: 140,
                minWidth: 110,
                renderCell: (params) => conditionChip(params.value),
            },
            text("statusChecks", "Status Checks", 160),
            number("positiveExamples", "Positive Examples", 150),
            number("negativeExamples", "Negative Examples", 160),
            text("upc", "UPC", 140),
            text("amazonUrl", "Amazon URL", 180),
            text("asin", "ASIN", 140),
            text("gtin", "GTIN", 140),
            text("walmartUrl", "Walmart URL", 180),
            text("wpid", "WPID", 140),
            money("msrpUsd", "MSRP (USD)"),
            number("velocityAmazonSalesPerMonth", "Velocity", 110),
            money("vistaMaxPrice", "Vista Max Price", 140),
            money("macMaxPrice", "MAC Max Price", 140),
            money("bidftaMaxPrice", "BidFTA Max Price", 150),
            money("ffPricing", "F+F Pricing"),
            money("fbMktPricing", "FB MKT Pricing", 140),
            money("purchasePriceEbay", "Purchase Price (eBay)", 170),
            number("weightLbs", "Weight (lbs)", 120),
            number("heightIn", "Height (in)", 110),
            number("widthIn", "Width (in)", 110),
            number("depthIn", "Depth (in)", 110),
            money("costRollup1x", "Cost Rollup 1x", 140),
            money("costRollup2x", "Cost Rollup 2x", 140),
            money("costRollup3x", "Cost Rollup 3x", 140),
            money("costRollup4x", "Cost Rollup 4x", 140),
            text("packagingType", "Packaging Type", 180),
            money("estShippingCost", "Est. Shipping Cost", 160),
            text("ebayShippingPolicy", "eBay Shipping Policy", 190),
            text("ebayDescriptionTemplate", "eBay Description Template", 200),
            text("walmartBlocked", "Walmart Blocked?", 140),
            money("suggestedEbayPrice", "Suggested eBay Price", 170),
            money("suggestedWalmartPrice", "Suggested Walmart Price", 190),
            money("suggestedMargin", "Suggested Margin", 150),
            number("catalogItems", "Invoice to Inventory", 160),
            text("createdBy", "Created By", 140),
            number("viablePackCount", "# of Viable Packs", 140),
            money("vistaPackPrice", "Vista Pack Price", 150),
            text("createdTime", "Created Time", 170),
            text("internalSkuId", "Internal SKU ID", 180),
            {
                field: "listedPlatforms",
                headerName: "Listed On",
                width: 220,
                minWidth: 160,
                sortable: false,
                valueGetter: (_value, row) => row.listedPlatformsText || "",
                renderCell: (params) =>
                    listedPlatformChips(params.row.listedPlatforms, {
                        needsEbayMigrate: Boolean(params.row.ebayNeedsMigrate),
                    }),
            },
            {
                field: "ebayNeedsMigrate",
                headerName: "eBay Migrate",
                width: 130,
                minWidth: 110,
                sortable: true,
                valueGetter: (_value, row) =>
                    row.ebayNeedsMigrate ? "Needs migrate" : "",
                renderCell: (params) =>
                    params.row.ebayNeedsMigrate ? (
                        <Chip
                            size="small"
                            label="Needs migrate"
                            sx={{
                                height: 22,
                                fontWeight: 700,
                                fontSize: 11,
                                borderRadius: "8px",
                                backgroundColor: "#fff7ed",
                                color: "#c2410c",
                                border: "1px solid #fdba74",
                            }}
                        />
                    ) : (
                        wrapCell("")
                    ),
            },
            text("walmartSkuLink", "Walmart SKU Link", 170),
            text("ebaySkuLink", "eBay SKU Link", 160),
            text("shopifySkuLink", "Shopify SKU Link", 160),
            text("shopifyProductId", "Shopify Product ID", 170),
            text("shopifyVariantId", "Shopify Variant ID", 170),
            money("walmartPrice", "Walmart Price", 140),
            money("ebayPrice", "eBay Price", 120),
            money("shopifyPrice", "Shopify Price", 140),
            text("helperFlagWalmartAutomation", "Helper Flag", 120),
            number("availableQuantityEbay", "Available Qty eBay", 150),
            number("availableQuantityWalmart", "Available Qty Walmart", 170),
            number("availableQuantityShopify", "Available Qty Shopify", 170),
            text("descriptionTemplateValueEbayConfig", "Description Template", 180),
            text("ebayConditionField", "eBay Condition", 140),
            percent("volumePricing2x", "Volume Pricing 2x"),
            percent("volumePricing3x", "Volume Pricing 3x"),
            percent("volumePricing4x", "Volume Pricing 4x"),
            number("table21", "Table 21", 100),
            text("ebayPublicInterfaceView", "eBay Public Interface", 170),
            number("ebayQuant30PlusDays", "eBay Quant 30+ Days", 160),
            number("walmartQuant30PlusDays", "WM Quant 30+ Days", 160),
            number("ebayOlderListingAge", "eBay Older Listing Age", 170),
            number("walmartOlderListingAge", "WM Older Listing Age", 170),
            number("totalQuantOnHand", "Total Quant on Hand", 160),
            text("oldestPurchaseDate", "Purchase Date Rollup", 170),
            text("expirationDateRollup", "Expiration Date Rollup", 170),
            text("inspectionWizardBuiltSku", "Wizard Built", 120),
            ...(!platformErrorsOnly
                ? [
                      text("platformErrorPlatforms", "Platform Error", 140),
                      text("platformErrorAt", "Platform Error At", 170),
                  ]
                : []),
        ];
    }, [platformErrorsOnly]);

    const columns = useMemo(
        () => layout.applyWidths(baseColumns),
        [baseColumns, layout.draftWidths, layout.applyWidths]
    );

    const openCreate = () => {
        setEditingId(null);
        setHighlightField(null);
        setRemigrateAfterSaveId(null);
        setDialogOpen(true);
    };

    const openEdit = (idOverride, options = {}) => {
        const id = idOverride ?? selectedIds[0];
        if (!id) return;
        setEditingId(id);
        setHighlightField(options.highlightField || null);
        setRemigrateAfterSaveId(options.remigrateAfterSave ? String(id) : null);
        setDialogOpen(true);
    };

    const deleteTargets = useMemo(() => {
        const idSet = new Set(selectedIds.map(String));
        return rows
            .filter((row) => idSet.has(String(row.id)))
            .map((row) => ({
                key: String(row.id),
                label:
                    row.listingField ||
                    row.internalSkuId ||
                    row.itemName ||
                    String(row.id),
            }));
    }, [rows, selectedIds]);

    const migrateEligibleIds = useMemo(() => {
        const idSet = new Set(selectedIds.map(String));
        return rows
            .filter((row) => idSet.has(String(row.id)) && isEbayMigrateEligible(row))
            .map((row) => String(row.id));
    }, [rows, selectedIds]);

    const campaignEligibleIds = useMemo(() => {
        const idSet = new Set(selectedIds.map(String));
        return rows
            .filter((row) => idSet.has(String(row.id)) && isEbayCampaignEligible(row))
            .map((row) => String(row.id));
    }, [rows, selectedIds]);

    const campaignTargets = useMemo(() => {
        const idSet = new Set(campaignTargetIds.map(String));
        return rows
            .filter((row) => idSet.has(String(row.id)))
            .map((row) => ({
                key: String(row.id),
                label:
                    row.listingField ||
                    row.internalSkuId ||
                    row.itemName ||
                    String(row.id),
            }));
    }, [rows, campaignTargetIds]);

    const openCampaignDialog = (ids) => {
        const uniqueIds = [...new Set((ids || []).map(String).filter(Boolean))];
        if (!uniqueIds.length) return;
        setCampaignTargetIds(uniqueIds);
        setCampaignDialogOpen(true);
    };

    const migrateEbayIds = async (ids) => {
        const uniqueIds = [...new Set((ids || []).map(String).filter(Boolean))];
        if (!uniqueIds.length) return;
        setMigrating(true);
        try {
            const result = await PostData(
                { ids: uniqueIds },
                "broylesllc/products/migrate-ebay"
            );
            if (!result?.success) {
                setSnack({
                    open: true,
                    color: "error",
                    message:
                        result?.error?.response?.data?.message ||
                        "Migrate to eBay failed.",
                });
                return;
            }
            const payload = result.data || {};
            const linked = Number(payload.linked) || 0;
            const skipped = Number(payload.skipped) || 0;
            const errors = Number(payload.errors) || 0;
            const errorRows = (payload.results || []).filter(
                (r) => r.status === "error"
            );
            const summary =
                `Linked ${linked}, skipped ${skipped}, errors ${errors}.`;
            if (errorRows.length) {
                const first = errorRows[0];
                setMigrateRecovery({
                    id: String(first.id || ""),
                    sku: String(first.sku || "").trim(),
                    message: String(first.message || "").trim(),
                });
                setSnack({
                    open: true,
                    color: linked ? "warning" : "error",
                    message: summary,
                });
            } else {
                setSnack({
                    open: true,
                    color: "success",
                    message: summary,
                });
            }
            setSelectionModel({ type: "include", ids: new Set() });
            refetch();
        } finally {
            setMigrating(false);
        }
    };

    const closeMigrateRecovery = () => setMigrateRecovery(null);

    const createEbayCampaign = async ({ discountPercent, startDate, endDate }) => {
        const uniqueIds = [...new Set(campaignTargetIds.map(String).filter(Boolean))];
        if (!uniqueIds.length) return;
        setCampaignSubmitting(true);
        try {
            const result = await PostData(
                {
                    ids: uniqueIds,
                    discountPercent,
                    startDate,
                    endDate,
                },
                "broylesllc/products/ebay-campaign"
            );
            if (!result?.success) {
                setSnack({
                    open: true,
                    color: "error",
                    message:
                        result?.error?.response?.data?.message ||
                        "Create eBay campaign failed.",
                });
                return;
            }
            const payload = result.data || {};
            setSnack({
                open: true,
                color: "success",
                message:
                    payload.message ||
                    `Campaign created${payload.promotionId ? ` (${payload.promotionId})` : ""}.`,
            });
            setCampaignDialogOpen(false);
            setCampaignTargetIds([]);
            setSelectionModel({ type: "include", ids: new Set() });
        } finally {
            setCampaignSubmitting(false);
        }
    };

    const rowMenuActions = useMemo(
        () => [
            {
                id: "copyShareUrl",
                label: "Copy share URL",
                disabled: (row) =>
                    !String(row?.listingField || row?.internalSkuId || "").trim(),
                onClick: async (row) => {
                    await copyTextToClipboard(
                        buildProductShareUrl(row, { tab: shareTab })
                    );
                    setSnack({
                        open: true,
                        color: "success",
                        message: "Link copied.",
                    });
                },
            },
            {
                id: "migrateEbay",
                label: "Migrate to eBay",
                disabled: (row) => migrating || !isEbayMigrateEligible(row),
                onClick: async (row) => {
                    await migrateEbayIds([row?.id]);
                },
            },
            {
                id: "ebayCampaign",
                label: "Add campaign",
                disabled: (row) =>
                    campaignSubmitting || !isEbayCampaignEligible(row),
                onClick: (row) => {
                    openCampaignDialog([row?.id]);
                },
            },
        ],
        // migrate/campaign handlers close over refetch; migrating/campaignSubmitting refresh disable
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [shareTab, migrating, campaignSubmitting]
    );

    const confirmDelete = async () => {
        if (!selectedIds.length) return;
        setDeleting(true);
        try {
            await Promise.all(
                selectedIds.map((id) =>
                    api.delete(`api/broylesllc/products/${id}`)
                )
            );
            setDeleteDialogOpen(false);
            setSnack({ open: true, color: "success", message: "Deleted." });
            setSelectionModel({ type: "include", ids: new Set() });
            refetch();
        } catch (err) {
            setSnack({
                open: true,
                color: "error",
                message: err?.response?.data?.message || "Delete failed.",
            });
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            {(error || (!load && !isLoading && data == null)) && (
                <Typography color="error" sx={{ mb: 1, fontSize: 13, px: 0.5 }}>
                    {error ? "Failed to load products." : null}
                </Typography>
            )}
            <div className="catalog-grid-shell">
                <div className="catalog-toolbar">
                    <TextField
                        size="small"
                        placeholder="Search SKU, name, ASIN…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={catalogSearchFieldSx}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9aa3ad" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {platformErrorsOnly ? (
                        <Box sx={{ display: "flex", gap: 0.75 }}>
                            {PLATFORM_ERROR_FILTERS.map((item) => {
                                const active = errorPlatformFilter === item.id;
                                return (
                                    <Button
                                        key={item.id}
                                        size="small"
                                        variant={active ? "contained" : "outlined"}
                                        onClick={() => setErrorPlatformFilter(item.id)}
                                        sx={active ? catalogPrimaryButtonSx : catalogQuietButtonSx}
                                    >
                                        {item.label}
                                    </Button>
                                );
                            })}
                        </Box>
                    ) : null}
                    {selectedIds.length > 0 ? (
                        <span className="catalog-selection-hint">
                            {selectedIds.length} selected
                        </span>
                    ) : null}
                    <div className="catalog-toolbar-spacer" />
                    <GridLayoutControls layout={layout} columns={baseColumns} />
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={!selectedIds.length || deleting}
                        startIcon={
                            deleting ? (
                                <CircularProgress size={14} color="error" />
                            ) : (
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                            )
                        }
                        onClick={() => setDeleteDialogOpen(true)}
                        sx={catalogDangerButtonSx}
                    >
                        Delete
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={selectedIds.length !== 1}
                        startIcon={<EditOutlinedIcon fontSize="small" />}
                        onClick={() => openEdit()}
                        sx={catalogQuietButtonSx}
                    >
                        Edit
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={
                            (!migrateEligibleIds.length &&
                                !campaignEligibleIds.length) ||
                            migrating ||
                            campaignSubmitting
                        }
                        startIcon={
                            migrating || campaignSubmitting ? (
                                <CircularProgress size={14} />
                            ) : (
                                <StorefrontOutlinedIcon fontSize="small" />
                            )
                        }
                        endIcon={<ArrowDropDownIcon fontSize="small" />}
                        onClick={(e) => setPlatformMenuAnchor(e.currentTarget)}
                        sx={{ ...catalogQuietButtonSx, whiteSpace: "nowrap" }}
                    >
                        Platform Actions
                    </Button>
                    <Menu
                        anchorEl={platformMenuAnchor}
                        open={Boolean(platformMenuAnchor)}
                        onClose={() => setPlatformMenuAnchor(null)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                        <MenuItem
                            disabled={!migrateEligibleIds.length || migrating}
                            onClick={() => {
                                setPlatformMenuAnchor(null);
                                migrateEbayIds(migrateEligibleIds);
                            }}
                        >
                            Migrate to eBay
                        </MenuItem>
                        <MenuItem
                            disabled={
                                !campaignEligibleIds.length || campaignSubmitting
                            }
                            onClick={() => {
                                setPlatformMenuAnchor(null);
                                openCampaignDialog(campaignEligibleIds);
                            }}
                        >
                            Add campaign
                        </MenuItem>
                    </Menu>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddOutlinedIcon fontSize="small" />}
                        onClick={openCreate}
                        sx={catalogPrimaryButtonSx}
                    >
                        Add Product
                    </Button>
                </div>
                <GridFilterBar {...filterBar} />
                <div className="catalog-grid-main">
                    <DataGrid
                        {...appDataGridDefaultProps}
                        loading={Boolean(load || isLoading)}
                        rows={filteredRows}
                        columns={columns}
                        checkboxSelection
                        disableRowSelectionOnClick
                        rowSelectionModel={selectionModel}
                        onRowSelectionModelChange={setSelectionModel}
                        sortingMode="server"
                        sortModel={
                            layout.sortModel?.length ? layout.sortModel : DEFAULT_PRODUCT_SORT
                        }
                        onSortModelChange={(model) => {
                            layout.onSortModelChange(
                                model?.length ? model : DEFAULT_PRODUCT_SORT
                            );
                            setPaginationModel((prev) => ({ ...prev, page: 0 }));
                        }}
                        columnVisibilityModel={layout.columnVisibilityModel}
                        onColumnVisibilityModelChange={layout.onColumnVisibilityModelChange}
                        onColumnWidthChange={layout.onColumnWidthChange}
                        onRowDoubleClick={(params) => openEdit(params.id)}
                        paginationMode={clientOnlyFilterActive ? "client" : "server"}
                        rowCount={clientOnlyFilterActive ? filteredRows.length : rowCount}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[25, 50, 100]}
                        slotProps={gridSlotProps}
                        sx={catalogDataGridSx}
                    />
                </div>
            </div>

            <GridRowContextMenu
                menu={rowMenu}
                onClose={closeRowMenu}
                actions={rowMenuActions}
            />

            <ProductFormDialog
                open={dialogOpen}
                productId={editingId}
                highlightField={highlightField}
                onClose={() => {
                    setDialogOpen(false);
                    setEditingId(null);
                    setHighlightField(null);
                    setRemigrateAfterSaveId(null);
                }}
                onSaved={async () => {
                    refetch();
                    setSelectionModel({ type: "include", ids: new Set() });
                    const remigrateId = remigrateAfterSaveId;
                    setRemigrateAfterSaveId(null);
                    setHighlightField(null);
                    if (remigrateId) {
                        await migrateEbayIds([remigrateId]);
                    }
                }}
                onNotify={({ color, message }) =>
                    setSnack({ open: true, color, message })
                }
            />

            <ConfirmDeleteDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={confirmDelete}
                confirming={deleting}
                title={
                    deleteTargets.length === 1
                        ? "Delete product?"
                        : `Delete ${deleteTargets.length} products?`
                }
                description="This cannot be undone. The following will be removed:"
                items={deleteTargets}
            />

            <EbayCampaignDialog
                open={campaignDialogOpen}
                onClose={() => {
                    if (campaignSubmitting) return;
                    setCampaignDialogOpen(false);
                    setCampaignTargetIds([]);
                }}
                onConfirm={createEbayCampaign}
                items={campaignTargets}
                submitting={campaignSubmitting}
            />

            <MigrateEbayRecoveryDialog
                open={Boolean(migrateRecovery)}
                onClose={closeMigrateRecovery}
                sku={migrateRecovery?.sku || ""}
                message={migrateRecovery?.message || ""}
                onChooseParent={() => {
                    const id = migrateRecovery?.id;
                    closeMigrateRecovery();
                    if (!id) return;
                    openEdit(id, {
                        highlightField: "ebayListingId",
                        remigrateAfterSave: true,
                    });
                }}
                onRetryChild={() => {
                    const id = migrateRecovery?.id;
                    closeMigrateRecovery();
                    if (id) migrateEbayIds([id]);
                }}
                onNotOnEbay={() => {
                    closeMigrateRecovery();
                    setSnack({
                        open: true,
                        color: "info",
                        message:
                            "This SKU isn’t linked to an eBay listing yet. Create it via Inventory Upload, then migrate if needed.",
                    });
                }}
            />

            {snack.open && (
                <SnackbarEx
                    color={snack.color}
                    message={snack.message}
                    open={snack.open}
                    close={() => setSnack({ open: false, color: "", message: "" })}
                />
            )}
        </>
    );
}
